import { useEffect, useMemo, useState } from "react";
import type { ReactElement } from "react";
import { useSelector } from "react-redux";
import { Button, Form, Input, Modal, Result, Select, Space, Table, Tag, message } from "antd";
import type { ColumnsType } from "antd/es/table";
import { deleteShopReturnAddress, formatReturnAddress, listShopReturnAddresses } from "@/api/shopReturnAddress";
import type { ShopReturnAddress } from "@/api/shopReturnAddress";
import { canManageReturnAddress } from "@/utils/returnAddressAccess";
import type { ReturnAddressSessionState } from "@/utils/returnAddressAccess";
import { formatDateTime } from "@/utils/dateTime";
import { ReturnAddressEditor } from "./ReturnAddressEditor";

interface Filters { keyword?: string; status?: number }
export default function PlatformReturnAddress(): ReactElement {
  const session = useSelector((state: ReturnAddressSessionState) => state.userInfo.value);
  const allowed = canManageReturnAddress(session);
  const [form] = Form.useForm<Filters>();
  const [rows, setRows] = useState<ShopReturnAddress[]>([]);
  const [loading, setLoading] = useState(false);
  const [revision, setRevision] = useState(0);
  const [filters, setFilters] = useState<Filters>({});
  const [page, setPage] = useState(1);
  const [editor, setEditor] = useState<{ address: ShopReturnAddress | null } | null>(null);
  const [deleting, setDeleting] = useState(false);
  useEffect(() => {
    if (!allowed) return;
    const controller = new AbortController();
    setLoading(true);
    void listShopReturnAddresses(controller.signal).then(({ data }) => {
      if (!controller.signal.aborted) setRows((data ?? []).filter((address) => address.merchantId === 0));
    }).catch(() => {
      // 请求层统一提示失败，可重新查询，不在页面重复展示错误提示条。
      if (!controller.signal.aborted) setRows([]);
    }).finally(() => { if (!controller.signal.aborted) setLoading(false); });
    return (): void => controller.abort();
  }, [allowed, revision]);
  const filteredRows = useMemo(() => rows.filter((address) =>
    (!filters.keyword || [address.receiverName, address.receiverPhone, formatReturnAddress(address)].some((value) => value.includes(filters.keyword ?? ""))) &&
    (filters.status === undefined || address.status === filters.status)
  ), [rows, filters]);
  const handleRefresh = (): void => { setRevision((value) => value + 1); };
  const handleSearch = (values: Filters): void => { setFilters({ ...values, keyword: values.keyword?.trim() }); setPage(1); handleRefresh(); };
  const handleReset = (): void => { form.resetFields(); handleSearch({}); };
  const handleSaved = (): void => { setEditor(null); handleRefresh(); };
  const handleDelete = (address: ShopReturnAddress): void => {
    Modal.confirm({
      title: "删除退货地址", content: `确认删除“${address.receiverName}”的退货地址？`, okText: "删除", cancelText: "取消", okButtonProps: { danger: true },
      onOk: async (): Promise<void> => {
        setDeleting(true);
        try { await deleteShopReturnAddress(address.id); void message.success("删除成功"); handleRefresh(); }
        finally { setDeleting(false); }
      },
    });
  };
  const columns: ColumnsType<ShopReturnAddress> = [
    { title: "ID", dataIndex: "id", width: 80 },
    { title: "收件人", dataIndex: "receiverName", width: 120 },
    { title: "收件人电话", dataIndex: "receiverPhone", width: 150 },
    { title: "省市区", key: "region", width: 260, render: (_: unknown, address: ShopReturnAddress): string =>
      [address.provinceName, address.cityName, address.districtName].filter(Boolean).join(" / ") || "-" },
    { title: "详细地址", dataIndex: "detailAddress", width: 260, render: (value?: string): string => value || "-" },
    { title: "默认地址", dataIndex: "isDefault", width: 100, render: (value: number): ReactElement => <Tag color={value === 1 ? "blue" : undefined}>{value === 1 ? "是" : "否"}</Tag> },
    { title: "状态", dataIndex: "status", width: 100, render: (value: number): ReactElement => <Tag color={value === 1 ? "green" : undefined}>{value === 1 ? "正常" : value === 0 ? "停用" : "未知"}</Tag> },
    { title: "排序", dataIndex: "sort", width: 80 },
    { title: "创建时间", dataIndex: "createTime", render: formatDateTime, width: 180 },
    { title: "操作", key: "actions", fixed: "right", width: 150, render: (_: unknown, address: ShopReturnAddress): ReactElement => <Space>
      {canManageReturnAddress(session, "edit") && <Button size="small" disabled={loading || deleting} onClick={() => setEditor({ address })}>编辑</Button>}
      {canManageReturnAddress(session, "remove") && <Button size="small" danger disabled={loading || deleting} onClick={() => handleDelete(address)}>删除</Button>}
    </Space> },
  ];
  if (!allowed) return <Result status="403" title="无权访问平台退货地址管理" subTitle="此功能仅供已授权的总后台用户使用" />;
  return <div>
    <Form form={form} layout="inline" onFinish={handleSearch} style={{ gap: 12, marginBottom: 16 }}>
      <Form.Item name="keyword" label="地址信息"><Input allowClear placeholder="收件人、电话或地址" /></Form.Item>
      <Form.Item name="status" label="状态"><Select allowClear placeholder="请选择状态" style={{ width: 140 }} options={[{ label: "正常", value: 1 }, { label: "停用", value: 0 }]} /></Form.Item>
      <Space><Button type="primary" htmlType="submit">查询</Button><Button onClick={handleReset}>重置</Button></Space>
    </Form>
    <Space style={{ marginBottom: 16 }}>
      {canManageReturnAddress(session, "add") && <Button type="primary" onClick={() => setEditor({ address: null })}>新增退货地址</Button>}
    </Space>
    <Table<ShopReturnAddress> rowKey="id" bordered size="small" loading={loading} columns={columns} dataSource={filteredRows} scroll={{ x: "max-content" }}
      pagination={{ current: Math.min(page, Math.max(1, Math.ceil(filteredRows.length / 10))), pageSize: 10, total: filteredRows.length, showSizeChanger: false, onChange: setPage }} />
    {editor && <ReturnAddressEditor address={editor.address} onCancel={() => setEditor(null)} onSuccess={handleSaved} />}
  </div>;
}
